import { useState } from "react";
import { Eraser, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, DOMAINES, domaineLabel, errMsg, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** Purge ciblée des tickets de test : simulation obligatoire avant toute suppression. */
export const PurgeTickets = ({ tickets, onPurge }) => {
  const [ouvert, setOuvert] = useState(false);
  const [axonautId, setAxonautId] = useState("");
  const [domaine, setDomaine] = useState("");
  const [avant, setAvant] = useState("");
  const [contient, setContient] = useState("");
  const [apercu, setApercu] = useState(null);
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);

  const clients = Array.from(
    new Map(tickets.filter((t) => t.axonaut_id).map((t) => [t.axonaut_id, t.client?.nom || t.axonaut_id])).entries()
  );

  const filtres = () => ({
    axonaut_id: axonautId || null,
    domaine: domaine || null,
    avant: avant ? new Date(avant).toISOString() : null,
    contient: contient.trim() || null,
  });

  const simuler = async () => {
    setBusy(true);
    setApercu(null);
    try {
      const { data } = await api.post("/admin/tickets/purge", { ...filtres(), simulation: true });
      setApercu(data);
      if (!data.total) toast.info("Aucun ticket ne correspond à ces filtres");
    } catch (e) {
      toast.error(errMsg(e, "Simulation impossible"));
    } finally {
      setBusy(false);
    }
  };

  const purger = async () => {
    if (confirmation !== "SUPPRIMER") return toast.error("Saisissez SUPPRIMER pour confirmer");
    setBusy(true);
    try {
      const { data } = await api.post("/admin/tickets/purge", { ...filtres(), simulation: false, confirmation });
      toast.success(`${data.total} ticket(s) supprimé(s) définitivement`);
      setApercu(null);
      setConfirmation("");
      onPurge();
    } catch (e) {
      toast.error(errMsg(e, "Purge impossible"));
    } finally {
      setBusy(false);
    }
  };

  if (!ouvert) {
    return (
      <Button data-testid="purge-open-btn" variant="outline" size="sm" onClick={() => setOuvert(true)} className="text-slate-600">
        <Eraser size={14} className="mr-2" /> Purger des tickets de test
      </Button>
    );
  }

  return (
    <div className="rounded-xl border border-amber-300 bg-amber-50/60 p-4 space-y-3" data-testid="purge-panel">
      <div className="flex items-center justify-between gap-3">
        <div className="bk-label flex items-center gap-2 text-amber-700">
          <ShieldAlert size={13} /> Purge des tickets de test
        </div>
        <button data-testid="purge-close-btn" onClick={() => { setOuvert(false); setApercu(null); }} className="text-xs text-slate-500 hover:text-slate-900">
          Fermer
        </button>
      </div>
      <p className="text-xs text-slate-600">
        Filtrez, simulez, vérifiez la liste, puis confirmez. La suppression est définitive et retire aussi les
        notifications et les logs webhook associés. Sans filtre, TOUS les tickets sont concernés.
      </p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
        <select
          data-testid="purge-client-select"
          value={axonautId}
          onChange={(e) => { setAxonautId(e.target.value); setApercu(null); }}
          className="h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white"
        >
          <option value="">Tous les clients</option>
          {clients.map(([id, nom]) => (
            <option key={id} value={id}>{nom}</option>
          ))}
        </select>
        <select
          data-testid="purge-domaine-select"
          value={domaine}
          onChange={(e) => { setDomaine(e.target.value); setApercu(null); }}
          className="h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white"
        >
          <option value="">Tous les domaines</option>
          {DOMAINES.map((d) => (
            <option key={d.key} value={d.key}>{d.label}</option>
          ))}
        </select>
        <Input
          data-testid="purge-avant-input"
          type="date"
          value={avant}
          onChange={(e) => { setAvant(e.target.value); setApercu(null); }}
          className="h-10 bg-white"
        />
        <Input
          data-testid="purge-contient-input"
          value={contient}
          onChange={(e) => { setContient(e.target.value); setApercu(null); }}
          placeholder="Contient (ex : TEST)"
          className="h-10 bg-white"
        />
      </div>

      <Button data-testid="purge-simuler-btn" onClick={simuler} disabled={busy} size="sm" variant="outline" className="bg-white">
        {busy ? "Analyse…" : "Simuler la purge"}
      </Button>

      {apercu && (
        <div className="space-y-2" data-testid="purge-apercu">
          <div className="text-sm font-semibold text-slate-900" data-testid="purge-total">
            {apercu.total} ticket(s) seront supprimés
          </div>
          {apercu.apercu?.length > 0 && (
            <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
              {apercu.apercu.map((t) => (
                <div key={t.reference} data-testid={`purge-ligne-${t.reference}`} className="px-3 py-2 text-xs text-slate-600">
                  <span className="font-mono-bk text-slate-900">{t.reference}</span> · {domaineLabel(t.domaine)} · {t.client} ·{" "}
                  {fmtDate(t.created_at)}
                  {t.description ? ` · ${t.description}` : ""}
                </div>
              ))}
              {apercu.total > apercu.apercu.length && (
                <div className="px-3 py-2 text-xs text-slate-400">… et {apercu.total - apercu.apercu.length} autre(s)</div>
              )}
            </div>
          )}
          {apercu.total > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <Input
                data-testid="purge-confirmation-input"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                placeholder="Saisissez SUPPRIMER"
                className="h-10 max-w-[200px] bg-white"
              />
              <Button
                data-testid="purge-confirmer-btn"
                onClick={purger}
                disabled={busy || confirmation !== "SUPPRIMER"}
                size="sm"
                className="bg-red-600 hover:bg-red-700"
              >
                <Eraser size={14} className="mr-2" /> Supprimer définitivement
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PurgeTickets;
